/**
 * PROGRESO.
 *
 * Responde a «¿estoy mejorando de verdad?» sin convertirse en un dashboard.
 *
 * SIN RACHA: convertiría «no romper el número» en el objetivo, y el Día 6
 * depende de que otra persona esté disponible.
 *
 * Orden visual (§26), de arriba abajo: dónde estoy · cuánto entrené · cómo
 * evolucionan mis áreas · mediciones · material. La comparación detallada se
 * abre aparte, para que esta pantalla se entienda de un vistazo.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';

import { NOMBRE_AREA } from '../../domain/modelo/areas';
import {
  ETIQUETA_MEDICION,
  medicionDeSemana,
  programaInicialCompletado,
  SEMANAS_MEDICION,
} from '../../domain/reglas/checkpoints';
import {
  areasRecurrentes,
  conversacionesHumanas,
  estadoDelBanco,
  evolucionDeAutoevaluaciones,
  minutosEntrenados,
  progresoDelCiclo,
  respaldosIA,
  sesionesHechas,
} from '../../domain/reglas/progreso';
import { semanaActual } from '../../domain/reglas/progresion';
import { bancoRepo } from '../../persistencia/bancoRepo';
import { sesionesRepo } from '../../persistencia/sesionesRepo';
import { Pantalla } from '../comunes/Pantalla';
import { Suave, Tarjeta } from '../comunes/Tarjeta';
import { Tendencia } from '../comunes/Tendencia';
import estilos from './Progreso.module.css';

export function Progreso() {
  const sesiones = useLiveQuery(() => sesionesRepo.listar(), [], undefined);
  const banco = useLiveQuery(() => bancoRepo.listar(), [], undefined);

  if (!sesiones || !banco) {
    return (
      <Pantalla titulo="Progreso">
        <Suave>Cargando…</Suave>
      </Pantalla>
    );
  }

  const completado = programaInicialCompletado(sesiones);
  const semana = semanaActual(sesiones);
  const ciclo = progresoDelCiclo(sesiones, semana);
  const { minutos, sesionesSinDuracion } = minutosEntrenados(sesiones);
  const hechas = sesionesHechas(sesiones);
  const humanas = conversacionesHumanas(sesiones);
  const respaldos = respaldosIA(sesiones);
  const material = estadoDelBanco(banco);
  const recurrentes = areasRecurrentes(sesiones, banco);
  const evolucion = evolucionDeAutoevaluaciones(sesiones);

  return (
    <Pantalla titulo="Progreso" tituloVisible>
      {/* ── 1. Dónde estoy ──────────────────────────────────────────── */}
      {completado ? (
        <Tarjeta>
          <p className={estilos.completado}>Programa inicial de 12 semanas completado.</p>
          <Suave>
            Hiciste el baseline y los tres checkpoints. Podés seguir entrenando con las
            mismas sesiones; lo que no hay todavía es un programa nuevo, y preferimos
            decirlo a inventarte una semana 13.
          </Suave>
        </Tarjeta>
      ) : (
        <Tarjeta>
          <p className={estilos.donde}>
            Semana <strong>{semana}</strong> · día {ciclo.hechos} de {ciclo.total}
          </p>
          <div
            className={estilos.barra}
            role="img"
            aria-label={`${ciclo.hechos} de ${ciclo.total} sesiones de esta semana`}
          >
            <div
              className={estilos.barraRelleno}
              style={{ width: `${(ciclo.hechos / ciclo.total) * 100}%` }}
            />
          </div>
        </Tarjeta>
      )}

      {/* ── 2. Cuánto entrené ───────────────────────────────────────── */}
      <div className={estilos.metricas}>
        <div className={estilos.metrica}>
          <strong>{hechas.length}</strong>
          <span>sesiones</span>
        </div>
        <div className={estilos.metrica}>
          <strong>{minutos}</strong>
          <span>minutos reales</span>
        </div>
        <div className={estilos.metrica}>
          <strong>{humanas}</strong>
          <span>con personas</span>
        </div>
      </div>

      {sesionesSinDuracion > 0 ? (
        <Suave>
          {sesionesSinDuracion === 1
            ? 'Una sesión no registró duración y no suma minutos.'
            : `${sesionesSinDuracion} sesiones no registraron duración y no suman minutos.`}{' '}
          Se cuenta el tiempo medido, nunca el planificado.
        </Suave>
      ) : null}

      {respaldos > 0 ? (
        <Suave>
          {respaldos === 1 ? 'Una vez' : `${respaldos} veces`} usaste ChatGPT como
          respaldo porque no conseguiste a nadie. Queda registrado aparte: no es lo
          mismo, y el número de arriba no lo cuenta.
        </Suave>
      ) : null}

      {/* ── 3. Cómo evolucionan mis áreas ───────────────────────────── */}
      <Tarjeta titulo="Cómo te vas sintiendo">
        {evolucion.length === 0 ? (
          <Suave>
            Todavía no cerraste ninguna sesión con autoevaluación. Cuando lo hagas, acá
            vas a ver cómo cambia cada área semana a semana.
          </Suave>
        ) : (
          <>
            <Suave>
              Es tu percepción al cerrar cada sesión, promediada por semana. 1 = me costó
              mucho, 5 = me salió con facilidad.
            </Suave>
            <ul className={estilos.tendencias}>
              {evolucion.map(({ area, puntos, soloUnDato }) => (
                <li key={area} className={estilos.tendencia}>
                  <span className={estilos.nombreArea}>{NOMBRE_AREA[area]}</span>
                  <Tendencia puntos={puntos} nombre={NOMBRE_AREA[area]} />
                  {soloUnDato ? null : (
                    <span className={estilos.detalle}>
                      {puntos.length} semanas con datos
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Tarjeta>

      {/* ── 4. Mediciones ───────────────────────────────────────────── */}
      <Tarjeta titulo="Mediciones">
        <ul className={estilos.hitos}>
          {SEMANAS_MEDICION.map((cuando) => {
            const medicion = medicionDeSemana(sesiones, cuando);
            return (
              <li key={cuando} className={estilos.hito}>
                <span>{ETIQUETA_MEDICION[cuando]}</span>
                <span className={medicion ? estilos.hecho : estilos.pendiente}>
                  {medicion ? 'hecho' : 'pendiente'}
                </span>
              </li>
            );
          })}
        </ul>
        <Link className={estilos.enlace} to="/progreso/comparacion">
          Comparar W0 · W4 · W8 · W12
        </Link>
      </Tarjeta>

      {/* ── 5. Material ─────────────────────────────────────────────── */}
      <Tarjeta titulo="Learning Bank">
        <p className={estilos.material}>
          <strong>{material.activos}</strong> activos ·{' '}
          <strong>{material.dominados}</strong> dominados ·{' '}
          <strong>{material.archivados}</strong> archivados
        </p>
        <Suave>
          Es material trabajado, no un nivel: más dominados significa que trabajaste más
          cosas, no que hables mejor. Lo dominado sale de la vista activa y del Día 4,
          pero no se borra nunca.
        </Suave>
      </Tarjeta>

      {recurrentes.length > 0 ? (
        <Tarjeta titulo="Lo que más aparece">
          <Suave>
            Un recuento llano, sin fórmula: elementos activos del banco, más las veces
            que marcaste el área como lo más difícil, más las correcciones que guardaste.
            La única puntuación de la aplicación es la del Día 5.
          </Suave>
          <ul className={estilos.recurrentes}>
            {recurrentes.map((recuento) => (
              <li key={recuento.area} className={estilos.recurrente}>
                <span className={estilos.nombreArea}>{NOMBRE_AREA[recuento.area]}</span>
                <span className={estilos.desglose}>
                  {recuento.elementos} del banco · {recuento.masDificil} veces «lo más
                  difícil» · {recuento.correcciones} correcciones
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      ) : null}
    </Pantalla>
  );
}
